import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, Search, Loader2 } from "lucide-react";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface EventChangeLogRow {
  id: string;
  workspaceId: string;
  clientId: string;
  invoiceId: string | null;
  fieldName: string;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  changedByUserId: string;
  changedByName: string | null;
  changedAt: string;
  source: string;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
}

const PAGE_SIZE = 50;

const EVENT_FIELD_OPTIONS = [
  { value: "__all", label: "All fields" },
  { value: "booking_created", label: "Booking created" },
  { value: "dateOfEvent", label: "Event Date" },
  { value: "eventTime", label: "Event Time" },
  { value: "eventRate", label: "Event Rate" },
  { value: "discount", label: "Discount" },
  { value: "address", label: "Address" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "eventDetails", label: "Event Details" },
];

function formatDate(s: string) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(s));
  } catch {
    return s;
  }
}

function sourceLabel(src: string) {
  if (src === "booking_orchestrate") return "Booking created";
  if (src === "invoice_patch") return "Invoice edit";
  if (src === "client_patch") return "Client edit";
  return src;
}

export default function PlatformEventAudit() {
  const [workspaceId, setWorkspaceId] = useState("__all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fieldFilter, setFieldFilter] = useState("__all");
  const [clientFilter, setClientFilter] = useState("");
  const [debouncedClient, setDebouncedClient] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedClient(clientFilter), 350);
    return () => clearTimeout(t);
  }, [clientFilter]);

  useEffect(() => {
    setPage(0);
  }, [workspaceId, debouncedSearch, fieldFilter, debouncedClient]);

  const { data: wsData } = useQuery<any>({
    queryKey: ["/api/platform/workspaces", { limit: 200 }],
    queryFn: () =>
      fetch("/api/platform/workspaces?limit=200&offset=0", { credentials: "include" }).then((r) =>
        r.json(),
      ),
  });
  const workspaces: Workspace[] = wsData?.workspaces ?? wsData ?? [];

  const { data, isLoading } = useQuery<{ rows: EventChangeLogRow[]; total: number }>({
    queryKey: [
      "/api/platform/event-audit",
      workspaceId,
      debouncedSearch,
      fieldFilter,
      debouncedClient,
      page,
    ],
    queryFn: async () => {
      if (!workspaceId || workspaceId === "__all") return { rows: [], total: 0 };
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (fieldFilter && fieldFilter !== "__all") params.set("fieldName", fieldFilter);
      if (debouncedClient) params.set("clientId", debouncedClient);
      const res = await fetch(
        `/api/platform/workspaces/${workspaceId}/event-change-logs?${params}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: !!workspaceId && workspaceId !== "__all",
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilter =
    debouncedSearch || (fieldFilter && fieldFilter !== "__all") || debouncedClient;

  return (
    <PlatformLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2.5">
            <History className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1
              className="text-2xl font-semibold tracking-tight"
              data-testid="text-page-title"
            >
              Event Audit Log
            </h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Field-level change history for all event and booking records. Select a workspace,
              then filter by field, user, or free-text.
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <Select
            value={workspaceId}
            onValueChange={setWorkspaceId}
          >
            <SelectTrigger className="h-9" data-testid="select-workspace-filter">
              <SelectValue placeholder="Select workspace…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All workspaces</SelectItem>
              {workspaces.map((ws) => (
                <SelectItem key={ws.id} value={ws.id}>
                  {ws.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 h-9"
              placeholder="Search fields, values, users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-event-audit-search"
            />
          </div>

          <Select value={fieldFilter} onValueChange={setFieldFilter}>
            <SelectTrigger className="h-9" data-testid="select-field-filter">
              <SelectValue placeholder="All fields" />
            </SelectTrigger>
            <SelectContent>
              {EVENT_FIELD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            className="h-9 font-mono text-sm"
            placeholder="Client ID…"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            data-testid="input-client-id-filter"
          />
        </div>

        {/* Body */}
        {workspaceId === "__all" ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <History className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-base font-medium">Select a workspace</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                Choose a workspace above to view its event change log.
              </p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground">
                {hasActiveFilter
                  ? "No matching changes found."
                  : "No event changes recorded yet for this workspace."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 border-b">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-2.5 font-medium whitespace-nowrap">When</th>
                      <th className="px-3 py-2.5 font-medium">Field</th>
                      <th className="px-3 py-2.5 font-medium">From</th>
                      <th className="px-3 py-2.5 font-medium">To</th>
                      <th className="px-3 py-2.5 font-medium">By</th>
                      <th className="px-3 py-2.5 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        data-testid={`row-event-audit-${row.id}`}
                        className="hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                          {formatDate(row.changedAt)}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {row.fieldLabel ?? row.fieldName}
                        </td>
                        <td
                          className="px-3 py-2 text-muted-foreground max-w-[140px] truncate"
                          title={row.oldValue ?? ""}
                        >
                          {row.oldValue ?? (
                            <span className="italic opacity-40">—</span>
                          )}
                        </td>
                        <td
                          className="px-3 py-2 max-w-[140px] truncate"
                          title={row.newValue ?? ""}
                        >
                          {row.newValue ?? (
                            <span className="italic opacity-40">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {row.changedByName ?? row.changedByUserId.slice(0, 8)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant="secondary"
                            className="text-[10px] font-normal"
                          >
                            {sourceLabel(row.source)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>
                {total.toLocaleString()} {total === 1 ? "change" : "changes"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-3"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  data-testid="button-event-audit-prev"
                >
                  Prev
                </Button>
                <span>
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-3"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="button-event-audit-next"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
